import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, CardHeader,
    Button, Spinner, Badge,
} from "reactstrap";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import useAuth from "./useAuth";
import { getMySchedule, getTeamSchedule } from "./ftoService";

const MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

// ─── Color map per status+type ────────────────────────────────────────────────
const dayStyle = (type, status) => {
    if (type === "COA") return { background: "#d1f2e4", color: "#0a3622" };
    if (status === "approved") return { background: "#dce4f5", color: "#0c447c" };
    if (status === "denied") return { background: "#f8d7da", color: "#842029" };
    return { background: "#fff3cd", color: "#856404" }; // pending
};

// ─── Expand request date range into individual day strings ───────────────────
const expandDays = (item) => {
    const days = [];
    const start = new Date(item.startDate + "T00:00:00");
    const end = item.endDate ? new Date(item.endDate + "T00:00:00") : start;
    const cur = new Date(start);
    while (cur <= end) {
        days.push(cur.toISOString().split("T")[0]);
        cur.setDate(cur.getDate() + 1);
    }
    return days;
};

// ─── Build day map: { "2026-04-06": { type, status, item } } ─────────────────
const buildDayMap = (items) => {
    const map = {};
    for (const item of items) {
        for (const day of expandDays(item)) {
            map[day] = { type: item.type, status: item.status, item };
        }
    }
    return map;
};

// ─── Single month grid ────────────────────────────────────────────────────────
const MonthGrid = ({ year, monthIdx, dayMap, onDayClick }) => {
    const firstDay = new Date(year, monthIdx, 1).getDay();
    const daysInMon = new Date(year, monthIdx + 1, 0).getDate();
    const today = new Date().toISOString().split("T")[0];
    const cells = [];

    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMon; d++) cells.push(d);

    return (
        <div style={{ border: "1px solid #e9ebec", borderRadius: 6, overflow: "hidden" }}>
            <div style={{
                background: "#f8f9fa", padding: "6px 10px", textAlign: "center",
                fontSize: 12, fontWeight: 600, color: "#405189",
                borderBottom: "1px solid #e9ebec",
            }}>
                {MONTHS[monthIdx]} {year}
            </div>
            <div style={{ padding: 6 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1 }}>
                    {DAY_NAMES.map(d => (
                        <div key={d} style={{
                            fontSize: 10, color: "#878a99", textAlign: "center",
                            padding: "2px 0", fontWeight: 500
                        }}>{d}</div>
                    ))}
                    {cells.map((d, i) => {
                        if (!d) return <div key={`e-${i}`} />;
                        const dateStr = `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                        const entry = dayMap[dateStr];
                        const isToday = dateStr === today;
                        return (
                            <div key={dateStr}
                                onClick={() => entry && onDayClick(entry.item)}
                                style={{
                                    fontSize: 11, textAlign: "center", padding: "3px 1px",
                                    borderRadius: 3, minHeight: 20, lineHeight: 1.4,
                                    cursor: entry ? "pointer" : "default",
                                    outline: isToday ? "2px solid #405189" : "none",
                                    outlineOffset: -2,
                                    ...(entry ? dayStyle(entry.type, entry.status) : {}),
                                }}
                            >{d}</div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const map = {
        approved: "bg-success-subtle text-success",
        pending: "bg-warning-subtle text-warning",
        denied: "bg-danger-subtle text-danger",
    };
    return <span className={`badge ${map[status] ?? "bg-secondary-subtle text-secondary"}`}>
        {status}
    </span>;
};

// ─── Detail popover card ──────────────────────────────────────────────────────
const DetailCard = ({ item, onClose }) => (
    <div style={{
        position: "absolute", zIndex: 100, top: 0, right: 0,
        background: "#fff", border: "1px solid #e9ebec", borderRadius: 8,
        padding: 16, width: 280, boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    }}>
        <div className="d-flex justify-content-between align-items-center mb-2">
            <span style={{ fontSize: 13, fontWeight: 600 }}>
                {item.type} #{item.id}
            </span>
            <button onClick={onClose} style={{
                border: "none", background: "none", cursor: "pointer",
                fontSize: 16, color: "#878a99", lineHeight: 1,
            }}>✕</button>
        </div>
        <table style={{ width: "100%", fontSize: 12 }}>
            <tbody>
                {[
                    ["Status", <StatusBadge status={item.status} />],
                    ["Start", item.startDate],
                    ["End", item.endDate ?? "—"],
                    ["Days", item.ftoCount || "—"],
                    ["Type", item.leaveType],
                    ["Comment", item.employeeComment || "—"],
                ].map(([lbl, val]) => (
                    <tr key={lbl}>
                        <td style={{ color: "#878a99", paddingBottom: 4, paddingRight: 8 }}>{lbl}</td>
                        <td style={{ paddingBottom: 4 }}>{val}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ─── List view ────────────────────────────────────────────────────────────────
const ListView = ({ items, navigate }) => (
    <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
                <tr>
                    {["#", "Type", "Start date", "End date", "Days", "Supervisor", "HR", "Status"].map(h => (
                        <th key={h} style={{ fontSize: 12 }}>{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {items.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-4">No requests found.</td></tr>
                ) : items.map(r => (
                    <tr key={`${r.type}-${r.id}`} style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/attendance/${r.type.toLowerCase()}/${r.id}`)}>
                        <td className="fw-medium text-primary" style={{ fontSize: 13 }}>{r.id}</td>
                        <td>
                            <span className={`badge ${r.type === "COA"
                                ? "bg-success-subtle text-success"
                                : "bg-primary-subtle text-primary"}`}>
                                {r.type === "COA" ? "COA" : r.leaveType}
                            </span>
                        </td>
                        <td style={{ fontSize: 13 }}>{r.startDate}</td>
                        <td style={{ fontSize: 13 }}>{r.endDate ?? "—"}</td>
                        <td style={{ fontSize: 13 }}>{r.ftoCount || "—"}</td>
                        <td><StatusBadge status={r.supervisorApproval === true ? "approved"
                            : r.supervisorApproval === false ? "denied" : "pending"} /></td>
                        <td><StatusBadge status={r.hrApproval === true ? "approved"
                            : r.hrApproval === false ? "denied" : "pending"} /></td>
                        <td><StatusBadge status={r.status} /></td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const SchedulePage = () => {
    const navigate = useNavigate();
    const auth = useAuth();

    const [year, setYear] = useState(new Date().getFullYear());
    const [view, setView] = useState("calendar");
    const [filter, setFilter] = useState("my");
    const [data, setData] = useState([]);
    const [teamData, setTeamData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    // ── Fetch ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setData([]);
        setTeamData([]);

        const fetch = async () => {
            try {
                if (filter === "my") {
                    const { data: res } = await getMySchedule(year);
                    if (!cancelled) setData(res?.items ?? []);
                } else {
                    const { data: res } = await getTeamSchedule(year);
                    if (!cancelled) {
                        setTeamData(res ?? []);
                        // flatten all items for calendar
                        setData((res ?? []).flatMap(r => r.items ?? []));
                    }
                }
            } catch {
                // silently fail
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetch();
        return () => { cancelled = true; };
    }, [year, filter]);

    // Reset page when year/filter/view changes
    useEffect(() => { setPage(1); }, [year, filter, view]);

    const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
    const currentPage = Math.min(page, totalPages);
    const paginated = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

    const dayMap = useMemo(() => buildDayMap(data), [data]);

    // ── Summary counts ─────────────────────────────────────────────────────────
    const ftoItems = data.filter(x => x.type === "FTO");
    const coaItems = data.filter(x => x.type === "COA");
    const totalFtoDays = ftoItems.reduce((s, x) => s + (x.ftoCount || 0), 0);
    const approvedDays = ftoItems.filter(x => x.status === "approved")
        .reduce((s, x) => s + (x.ftoCount || 0), 0);
    const pendingDays = ftoItems.filter(x => x.status === "pending")
        .reduce((s, x) => s + (x.ftoCount || 0), 0);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-calendar-line"
                    eyebrow="Attendance"
                    title="My Schedule"
                    subtitle="Your attendance schedule — calendar and list views."
                    tone="blue"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Personal Attendance Change", to: "/workflows?wf=pac" },
                        { label: "My Schedule" },
                    ]}
                />

                <Card>
                    <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2 py-3">
                        {/* Controls */}
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            {/* Year nav */}
                            <div className="d-flex align-items-center gap-1">
                                <Button size="sm" color="light" onClick={() => setYear(y => y - 1)}>‹</Button>
                                <span style={{ fontSize: 15, fontWeight: 600, minWidth: 44, textAlign: "center" }}>
                                    {year}
                                </span>
                                <Button size="sm" color="light" onClick={() => setYear(y => y + 1)}>›</Button>
                            </div>

                            {/* View toggle */}
                            <div className="d-flex border rounded overflow-hidden">
                                {["calendar", "list"].map(v => (
                                    <button key={v} onClick={() => setView(v)}
                                        className="btn btn-sm"
                                        style={{
                                            borderRadius: 0, fontSize: 12, fontWeight: 500,
                                            background: view === v ? "#405189" : "#fff",
                                            color: view === v ? "#fff" : "#878a99",
                                            border: "none", padding: "5px 14px",
                                        }}>
                                        {v === "calendar" ? "Calendar" : "List"}
                                    </button>
                                ))}
                            </div>

                            {/* My / Team filter */}
                            {(auth.isManager || auth.isSuperUser) && (
                                <div className="d-flex gap-2">
                                    {["my", "team"].map(f => (
                                        <button key={f} onClick={() => setFilter(f)}
                                            className="btn btn-sm rounded-pill"
                                            style={{
                                                fontSize: 12, fontWeight: 500,
                                                background: filter === f ? "#eef2ff" : "#fff",
                                                border: `1px solid ${filter === f ? "#405189" : "#e9ebec"}`,
                                                color: filter === f ? "#405189" : "#495057",
                                            }}>
                                            {f === "my" ? "My Schedule" : "My Team"}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Legend */}
                        <div className="d-flex gap-3 align-items-center flex-wrap">
                            {[
                                { label: "FTO Approved", bg: "#378add" },
                                { label: "FTO Pending", bg: "#f0c040" },
                                { label: "FTO Denied", bg: "#e06060" },
                                { label: "COA", bg: "#28a068" },
                            ].map(({ label, bg }) => (
                                <div key={label} className="d-flex align-items-center gap-1">
                                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: bg }} />
                                    <span style={{ fontSize: 11, color: "#878a99" }}>{label}</span>
                                </div>
                            ))}
                        </div>
                    </CardHeader>

                    <CardBody>
                        {/* Summary row */}
                        <Row className="g-3 mb-4">
                            {[
                                { label: "Total FTO days", val: totalFtoDays.toFixed(1), color: "#405189" },
                                { label: "Approved", val: approvedDays.toFixed(1), color: "#198754" },
                                { label: "Pending", val: pendingDays.toFixed(1), color: "#856404" },
                                { label: "COA requests", val: coaItems.length, color: "#0a3622" },
                            ].map(({ label, val, color }) => (
                                <Col xl={3} sm={6} key={label}>
                                    <div style={{ background: "#f8f9fa", borderRadius: 6, padding: "10px 14px" }}>
                                        <div style={{ fontSize: 11, color: "#878a99", marginBottom: 3 }}>{label}</div>
                                        <div style={{ fontSize: 22, fontWeight: 700, color }}>{val}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>

                        {loading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : view === "calendar" ? (
                            <div style={{ position: "relative" }}>
                                <div style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                                    gap: 14,
                                }}>
                                    {Array.from({ length: 12 }, (_, i) => (
                                        <MonthGrid key={i} year={year} monthIdx={i}
                                            dayMap={dayMap}
                                            onDayClick={(item) => setSelected(item)} />
                                    ))}
                                </div>
                                {selected && (
                                    <DetailCard item={selected} onClose={() => setSelected(null)} />
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Show entries */}
                                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <span style={{ fontSize: 13, color: "#878a99" }}>Show</span>
                                        <select className="form-select form-select-sm" style={{ width: 75 }}
                                            value={pageSize}
                                            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                                            {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                                        </select>
                                        <span style={{ fontSize: 13, color: "#878a99" }}>entries</span>
                                    </div>
                                    <span style={{ fontSize: 13, color: "#878a99" }}>
                                        Showing {data.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, data.length)} of {data.length} entries
                                    </span>
                                </div>

                                <ListView items={paginated} navigate={navigate} />

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-end gap-1 mt-3">
                                        <Button size="sm" color="light" disabled={currentPage === 1} onClick={() => goToPage(1)}>«</Button>
                                        <Button size="sm" color="light" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>‹</Button>
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                                            const p = start + i;
                                            return (
                                                <Button key={p} size="sm"
                                                    color={p === currentPage ? "primary" : "light"}
                                                    onClick={() => goToPage(p)}>{p}
                                                </Button>
                                            );
                                        })}
                                        <Button size="sm" color="light" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>›</Button>
                                        <Button size="sm" color="light" disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>»</Button>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default SchedulePage;